import { Component, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import * as JSZip from 'jszip';
import * as toGeoJSON from 'togeojson';

var turf = require('@turf/turf');
var geoParse = require('wellknown');

export interface FeedGeojsonUploadResult {
  fileName: string;
  content: string;
}

export interface FeedGeojsonUploadDialogData {
  target?: any;
  taskId?: string;
  sensorId?: string;
  customerId?: string;
}

@Component({
  selector: 'app-feed-geojson-upload-dialog',
  templateUrl: './feed-geojson-upload-dialog.component.html',
  styleUrls: ['./feed-geojson-upload-dialog.component.scss'],
})
export class FeedGeojsonUploadDialogComponent {
  selectedFile: File | null = null;
  fileContent = '';
  fileError = '';
  isDragOver = false;
  isParsingFile = false;
  isSubmitting = false;

  readonly maxFileSizeBytes = 5 * 1024 * 1024;
  private readonly supportedExtensions = ['geojson', 'json', 'kml', 'kmz'];
  private readonly supportedFormatLabel = 'GeoJSON, KML, or KMZ';
  private readonly boundaryToleranceKilometers = 0.001;

  constructor(
    private dialogRef: MatDialogRef<FeedGeojsonUploadDialogComponent, FeedGeojsonUploadResult | null>,
    private apiService: ApiService,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: FeedGeojsonUploadDialogData | null
  ) {}

  get selectedFileSizeLabel(): string {
    if (!this.selectedFile) {
      return '';
    }

    const sizeInKb = this.selectedFile.size / 1024;
    return sizeInKb >= 1024
      ? `${(sizeInKb / 1024).toFixed(2)} MB`
      : `${sizeInKb.toFixed(2)} KB`;
  }

  onFileSelected(event: Event): void {
    if (this.isParsingFile || this.isSubmitting) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.handleFile(file);
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if (this.isParsingFile || this.isSubmitting) {
      return;
    }

    const file = event.dataTransfer?.files?.[0] ?? null;
    this.handleFile(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  removeFile(fileInput?: HTMLInputElement, event?: Event): void {
    event?.stopPropagation();
    if (this.isParsingFile || this.isSubmitting) {
      return;
    }

    this.selectedFile = null;
    this.fileContent = '';
    this.fileError = '';
    if (fileInput) {
      fileInput.value = '';
    }
  }

  cancel(): void {
    if (this.isParsingFile || this.isSubmitting) {
      return;
    }

    this.dialogRef.close(null);
  }

  async submit(): Promise<void> {
    if (!this.selectedFile || !this.fileContent || this.fileError) {
      this.fileError = this.fileError || `Please upload a valid ${this.supportedFormatLabel} file.`;
      return;
    }

    try {
      this.isSubmitting = true;

      if (this.data?.taskId && this.data?.sensorId && this.data?.customerId) {
        const formData = new FormData();
        formData.append('taskid', String(this.data.taskId));
        formData.append('sensorid', String(this.data.sensorId));
        formData.append('customerid', String(this.data.customerId));
        formData.append('file', this.selectedFile, this.selectedFile.name);

        await lastValueFrom(this.apiService.saveMonitoringBaseline(formData));
      }

      this.dialogRef.close({
        fileName: this.selectedFile.name,
        content: this.fileContent,
      });
    } catch {
      this.fileError = 'Unable to save legal mining area. Please try again.';
    } finally {
      this.isSubmitting = false;
    }
  }

  private handleFile(file: File | null): void {
    this.fileError = '';
    this.fileContent = '';

    if (!file) {
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!this.supportedExtensions.includes(extension || '')) {
      this.selectedFile = null;
      this.fileError = `Please upload a ${this.supportedFormatLabel} file.`;
      return;
    }

    if (file.size > this.maxFileSizeBytes) {
      this.selectedFile = null;
      this.fileError = 'File size cannot exceed 5MB.';
      return;
    }

    this.selectedFile = null;
    this.isParsingFile = true;
    this.parseFileToGeojson(file)
      .then((parsedGeojson) => {
        const validationError = this.validateGeojsonWithinTarget(parsedGeojson);
        if (validationError) {
          this.selectedFile = null;
          this.fileError = validationError;
          return;
        }

        this.selectedFile = file;
        this.fileContent = JSON.stringify(parsedGeojson);
      })
      .catch((error) => {
        this.selectedFile = null;
        this.fileError = this.getFileParseErrorMessage(error);
      })
      .finally(() => {
        this.isParsingFile = false;
      });
  }

  private async parseFileToGeojson(file: File): Promise<any> {
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (extension === 'geojson' || extension === 'json') {
      try {
        const content = await this.readFileAsText(file);
        return JSON.parse(content);
      } catch {
        throw new Error('INVALID_GEOJSON');
      }
    }

    if (extension === 'kml') {
      try {
        const kmlText = await this.readFileAsText(file);
        return this.parseKmlToGeojson(kmlText);
      } catch {
        throw new Error('INVALID_KML');
      }
    }

    if (extension === 'kmz') {
      const zipContent = await JSZip.loadAsync(await file.arrayBuffer());
      const kmlEntry =
        zipContent.file('doc.kml') ||
        zipContent.file(/\.kml$/i)[0];

      if (!kmlEntry) {
        throw new Error('KMZ_MISSING_KML');
      }

      try {
        const kmlText = await kmlEntry.async('string');
        return this.parseKmlToGeojson(kmlText);
      } catch {
        throw new Error('INVALID_KMZ');
      }
    }

    throw new Error('UNSUPPORTED_FILE_TYPE');
  }

  private parseKmlToGeojson(kmlText: string): any {
    const parser = new DOMParser();
    const kmlDocument = parser.parseFromString(kmlText, 'text/xml');
    const parserErrors = kmlDocument.getElementsByTagName('parsererror');

    if (parserErrors.length > 0) {
      throw new Error('INVALID_KML_XML');
    }

    return toGeoJSON.kml(kmlDocument);
  }

  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('READ_FAILED'));
      reader.readAsText(file);
    });
  }

  private getFileParseErrorMessage(error: unknown): string {
    const errorMessage = error instanceof Error ? error.message : '';

    if (errorMessage === 'KMZ_MISSING_KML') {
      return 'Invalid KMZ file. It must contain at least one KML document.';
    }

    if (errorMessage === 'READ_FAILED') {
      this.selectedFile = null;
      this.fileError = 'Unable to read the selected file.';
      return this.fileError;
    }

    return `Please upload a valid ${this.supportedFormatLabel} file.`;
  }

  private validateGeojsonWithinTarget(geojson: any): string {
    const polygonGeometries = this.extractPolygonGeometries(geojson);
    if (polygonGeometries.length === 0) {
      return `Please upload a ${this.supportedFormatLabel} file containing Polygon or MultiPolygon geometry.`;
    }

    if (!this.data?.target) {
      return '';
    }

    const targetGeometry = this.parseTargetGeometry(this.data.target);
    if (!targetGeometry) {
      return `Area of interest (AOI) is missing or invalid. Please select an AOI before uploading ${this.supportedFormatLabel}.`;
    }

    const targetPolygons = this.getPolygonCoordinateSets(targetGeometry);
    if (targetPolygons.length === 0) {
      return `Area of interest (AOI) is missing or invalid. Please select an AOI before uploading ${this.supportedFormatLabel}.`;
    }

    const isInsideTarget = polygonGeometries.every((geometry) =>
      this.getPolygonCoordinateSets(geometry).every((polygonCoordinates) =>
        targetPolygons.some((targetCoordinates) =>
          this.isPolygonWithinTarget(polygonCoordinates, targetCoordinates)
        )
      )
    );

    return isInsideTarget ? '' : 'Uploaded geometry must be inside the area of interest (AOI).';
  }

  private parseTargetGeometry(target: any): any | null {
    if (!target) {
      return null;
    }

    if (target.type === 'Polygon' || target.type === 'MultiPolygon') {
      return target;
    }

    if (target.type === 'Feature') {
      return target.geometry;
    }

    if (target.geometry) {
      return target.geometry;
    }

    if (target.geoJSON) {
      return this.parseTargetGeometry(target.geoJSON);
    }

    if (typeof target === 'string') {
      const targetText = target.trim();
      if (!targetText) {
        return null;
      }

      try {
        return this.parseTargetGeometry(JSON.parse(targetText));
      } catch {}

      try {
        return geoParse(targetText);
      } catch {
        return null;
      }
    }

    return null;
  }

  private extractPolygonGeometries(geojson: any): any[] {
    if (!geojson) {
      return [];
    }

    if (geojson.type === 'FeatureCollection' && Array.isArray(geojson.features)) {
      return geojson.features.reduce((geometries, feature) => [
        ...geometries,
        ...this.extractPolygonGeometries(feature),
      ], []);
    }

    if (geojson.type === 'Feature') {
      return this.extractPolygonGeometries(geojson.geometry);
    }

    if (geojson.type === 'GeometryCollection' && Array.isArray(geojson.geometries)) {
      return geojson.geometries.reduce((geometries, geometry) => [
        ...geometries,
        ...this.extractPolygonGeometries(geometry),
      ], []);
    }

    if (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') {
      return [geojson];
    }

    return [];
  }

  private getPolygonCoordinateSets(geometry: any): number[][][][] {
    if (!geometry) {
      return [];
    }

    if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates)) {
      return [this.closePolygonRings(geometry.coordinates)];
    }

    if (geometry.type === 'MultiPolygon' && Array.isArray(geometry.coordinates)) {
      return geometry.coordinates.map((polygonCoordinates) =>
        this.closePolygonRings(polygonCoordinates)
      );
    }

    return [];
  }

  private closePolygonRings(polygonCoordinates: number[][][]): number[][][] {
    return polygonCoordinates.map((ring) => {
      if (!Array.isArray(ring) || ring.length === 0) {
        return ring;
      }

      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first?.[0] === last?.[0] && first?.[1] === last?.[1]) {
        return ring;
      }

      return [...ring, first];
    });
  }

  private isPolygonWithinTarget(polygonCoordinates: number[][][], targetCoordinates: number[][][]): boolean {
    try {
      const polygon = turf.polygon(polygonCoordinates);
      const target = turf.polygon(targetCoordinates);

      if (turf.booleanWithin(polygon, target)) {
        return true;
      }

      // Exported border coordinates can differ by sub-metre rounding.
      const targetWithBoundaryTolerance = turf.buffer(
        target,
        this.boundaryToleranceKilometers,
        { units: 'kilometers' }
      );

      return !!targetWithBoundaryTolerance &&
        turf.booleanWithin(polygon, targetWithBoundaryTolerance);
    } catch {
      return false;
    }
  }
}
